import {
  markAsTouched,
  QuestionBase,
  QuestionControlService,
  type ElementBase,
} from '@myre/ui-primitive-forms';
import { SeparatorComponent } from '@myre/ui-primitive-components';
import { getObjectEntries, isDefined } from '@myre/common-primitives';

import {
  Component,
  HostBinding,
  inject,
  ViewContainerRef,
  type ComponentRef,
  type OnInit,
  DestroyRef,
  viewChild,
  input,
  booleanAttribute,
  computed,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { map, take } from 'rxjs/operators';
import { combineLatest, of, type Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ModalService } from '../modal.service';
import { type IModalComponent, type IModalData } from '../modal.interface';

import { DefaultBodyComponent } from './templates/bodies/app-modal-body-default.component';
import { DefaultFooterComponent } from './templates/footers/app-modal-footer-default.component';
import { DefaultHeaderComponent } from './templates/headers/app-modal-header-default.component';

@Component({
  selector: 'myre-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, SeparatorComponent],
})
export class ModalComponent implements OnInit {
  private _destoryRef = inject(DestroyRef);
  private _modalService = inject(ModalService);
  private _questionControlService = inject(QuestionControlService);

  public body = viewChild('body', { read: ViewContainerRef });
  public header = viewChild('header', { read: ViewContainerRef });
  public footer = viewChild('footer', { read: ViewContainerRef });

  public modal = input<IModalData>();

  public first = input(false, { transform: booleanAttribute });

  protected isFull = computed(() => this.modal().size === 'full');
  protected isLarge = computed(() => this.modal().size === 'large');
  protected isMedium = computed(() => this.modal().size === 'medium');

  protected isInfo = computed(() => this.modal().type === 'info');
  protected isOther = computed(() => this.modal().type === 'other');
  protected isWarning = computed(() => this.modal().type === 'warning');
  protected isSidebar = computed(() => this.modal().type === 'sidebar');

  protected isLeftSidebar = computed(() => this.modal().sidebarFrom === 'left');
  protected isRightSidebar = computed(
    () => this.modal().sidebarFrom === 'right',
  );

  public sidebarWidth = computed(() => this.modal().sidebarWidth);
  public closeButtonWidth = computed(() => {
    const paddings = 16;
    const width = this.modal().sidebarWidth ?? 0;

    return width + paddings;
  });

  public elements$: Observable<ElementBase[]>;
  public isLoading$: Observable<boolean>;

  public ngOnInit(): void {
    if (this.modal().type === 'warning' || this.modal().type === 'info') {
      this.modal().header = undefined;
      this.modal().size = 'medium';

      const prefix = '_system.yesNoModal';
      const cancel = this.modal().footer?.data.cancel || `${prefix}.cancel`;
      const confirm = this.modal().footer?.data.ok;

      if (this.modal().type === 'warning') {
        this.modal().footer = {
          data: {
            cancel,
            confirmWarning: confirm || `${prefix}.confirmWarning`,
          },
        };
      } else {
        this.modal().footer = {
          data: {
            cancel,
            confirmInfo: confirm || `${prefix}.confirmInfo`,
          },
        };
      }
    }

    const headerComponentRef: ComponentRef<IModalComponent> =
      this.header().createComponent(
        this.modal().header && this.modal().header.component
          ? this.modal().header.component
          : DefaultHeaderComponent,
      );

    const { elementInjector = undefined, environmentInjector = undefined } =
      this.modal().body || {};

    const bodyComponenRef: ComponentRef<IModalComponent> =
      this._injectDataToSection(
        this.body().createComponent(
          this.modal().body?.component ?? DefaultBodyComponent,
          {
            ...(environmentInjector && { environmentInjector }),
            ...(elementInjector && { injector: elementInjector }),
          },
        ),
        this.modal()?.body?.data,
      );

    const footerComponentRef: ComponentRef<IModalComponent> =
      this.footer().createComponent(
        this.modal().footer && this.modal().footer.component
          ? this.modal().footer.component
          : DefaultFooterComponent,
      );

    const headerComponent: IModalComponent = headerComponentRef.instance;
    const bodyComponent: IModalComponent = bodyComponenRef.instance;
    const footerComponent: IModalComponent = footerComponentRef.instance;

    if (this.modal().header) {
      this.modal().header.componentRef = headerComponentRef;
    }

    if (this.modal().body) {
      this.modal().body.componentRef = bodyComponenRef;
    }

    if (this.modal().footer) {
      this.modal().footer.componentRef = footerComponentRef;
    }

    if (this.modal().body && this.modal().body.component) {
      Object.defineProperty(this.modal().context, 'formGroup', {
        get(): UntypedFormGroup {
          return bodyComponent.form;
        },
      });
    } else {
      this.elements$ = this.modal().context.elements || of([]);

      this.isLoading$ = this.elements$.pipe(
        map(els => {
          if (!els) {
            return false;
          }

          return !els.length;
        }),
      );

      bodyComponent.isLoading$ = this.isLoading$;
      footerComponent.isLoading$ = this.isLoading$;

      this.elements$
        .pipe(takeUntilDestroyed(this._destoryRef))
        .subscribe(els => {
          this.modal().context.formGroup =
            this._questionControlService.generateForm(els || []);
        });
    }

    headerComponent.uid = this.modal().uid;
    bodyComponent.uid = this.modal().uid;
    footerComponent.uid = this.modal().uid;
  }

  public get formGroup(): UntypedFormGroup {
    if (this.modal && this.modal().context && this.modal().context.formGroup) {
      return this.modal().context.formGroup;
    }

    return new UntypedFormGroup({});
  }

  public onSubmit(): void {
    this.elements$ = this.modal().context.elements || of([]);
    combineLatest([this.elements$])
      .pipe(takeUntilDestroyed(this._destoryRef), take(1))
      .subscribe(([els]: [ElementBase<any>[]]) => {
        els ||= [];

        const values = this._questionControlService.toValues(els, true);

        if (this.formGroup.valid) {
          if (
            this.modal().context &&
            'function' === typeof this.modal().context.onSubmit
          ) {
            return this.modal().context.onSubmit(
              values,
              els.filter(el => el instanceof QuestionBase),
            );
          }
        } else {
          markAsTouched(this.formGroup);
        }
      });
  }

  public closeModal(): void {
    this._modalService.close(this.modal().uid);
  }

  @HostBinding('style.left.px')
  public get leftOffset(): number | string {
    if (this.isLeftSidebar()) {
      return this.modal().sidebarOffset;
    }

    return 'unset';
  }

  @HostBinding('style.right.px')
  public get rightOffset(): number | string {
    if (this.isRightSidebar()) {
      return this.modal().sidebarOffset;
    }

    return 'unset';
  }

  // TODO: Migrate to inputBindings after upgrading the project to Angular ≥ 20.
  protected _injectDataToSection<C = unknown>(
    ref: ComponentRef<C>,
    data: Record<string, unknown> = {},
  ): ComponentRef<C> {
    if (ref && isDefined(data)) {
      if (Array.isArray(data) || typeof data !== 'object') {
        ref.setInput('data', data);
      } else {
        for (const [key, value] of getObjectEntries(data)) {
          ref.setInput(key, value);
        }
      }
    }

    return ref;
  }
}
